import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faTag, faRobot, faSpinner, faLightbulb, faGlobe, faCalendar, faClock, faRulerCombined } from '@fortawesome/free-solid-svg-icons';
import Calendar from 'react-calendar';
import { UNIT_TYPES, getSubtypes } from '../config/unitOfMeasure';
import { aiAPI, api, resolveFileUrl } from '../services/api';
import { usePlanLimits } from '../hooks/usePlanLimits';
import LimitReachedModal from './LimitReachedModal';
import { PlatformPlanType, PLATFORM_PLANS } from '../types/plans';
import useI18n from '../hooks/useI18n';
import '../styles/ItemModal.css';

// Get user's timezone
const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface Currency {
  code: string;
  label: string;
  symbol: string;
}

interface Item {
  id?: string;
  name: string;
  description?: string;
  categoryId?: string;
  condition: string;
  location?: string;
  purchasePlace?: string;
  purchasePrice?: number | string;
  purchaseCurrency: string;
  purchaseDate?: string;
  purchaseTime?: string;
  purchaseTimezone?: string;
  imageUrl?: string;
  images?: Array<{ id: string; url: string; isPrimary?: boolean; order?: number }>;
  tags?: string[];
  unitType?: string;
  unitSubtype?: string;
  unitQuantity?: number | string;
}

interface CategorySuggestion {
  id: string;
  name: string;
  confidence: number;
  icon?: string;
  color?: string;
}

interface ItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: Omit<Item, 'id'>, files?: File[]) => Promise<void>;
  item?: Item | null;
  categories: Category[];
  currencies: Currency[];
}

const ItemModal = ({ isOpen, onClose, onSave, item, categories, currencies }: ItemModalProps) => {
  const { t } = useI18n();
  const tc = (name: string) => { const key = `categoryName.${name}` as any; const translated = t(key); return translated !== key ? translated : name; };
  const { user } = useSelector((state: RootState) => state.auth);
  const { checkImageLimit, showLimitModal, limitModalType, limitCurrent, limitMax, closeLimitModal, currentPlan } = usePlanLimits((user?.planType as PlatformPlanType) || 'FREE');
  const planType = (user?.planType as PlatformPlanType) || 'FREE';
  const maxImages = PLATFORM_PLANS[planType]?.features?.maxImagesPerItem ?? 5;

  const [formData, setFormData] = useState<Omit<Item, 'id'>>({
    name: '',
    description: '',
    categoryId: '',
    condition: 'N/A',
    location: '',
    purchasePlace: '',
    purchasePrice: '',
    purchaseCurrency: 'USD',
    purchaseDate: '',
    purchaseTime: '',
    purchaseTimezone: getUserTimezone(),
    tags: [],
    unitType: '',
    unitSubtype: '',
    unitQuantity: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<Array<{ id: string; url: string; isPrimary?: boolean }>>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [categorySuggestions, setCategorySuggestions] = useState<CategorySuggestion[]>([]);
  const [suggestingCategories, setSuggestingCategories] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [purchaseDateValue, setPurchaseDateValue] = useState<Date | null>(null);
  const [purchaseTimeValue, setPurchaseTimeValue] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  // Debounce timer for AI suggestions
  const fetchCategorySuggestions = useCallback(async (name: string, description: string) => {
    if (!name || name.length < 3) {
      setCategorySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSuggestingCategories(true);
    try {
      const response = await aiAPI.suggestCategories(name, description);
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setCategorySuggestions(response.data.suggestions);
        setShowSuggestions(true);
      } else {
        setCategorySuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Failed to get category suggestions:', error);
      setCategorySuggestions([]);
    } finally {
      setSuggestingCategories(false);
    }
  }, []);

  // Debounced effect for fetching suggestions when name/description changes
  useEffect(() => {
    if (!item && formData.name) {
      const timer = setTimeout(() => {
        fetchCategorySuggestions(formData.name, formData.description || '');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.name, formData.description, item, fetchCategorySuggestions]);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        categoryId: item.categoryId || '',
        condition: item.condition || 'N/A',
        location: item.location || '',
        purchasePlace: item.purchasePlace || '',
        purchasePrice: item.purchasePrice || '',
        purchaseCurrency: item.purchaseCurrency || 'USD',
        purchaseDate: item.purchaseDate || '',
        purchaseTime: item.purchaseTime || '',
        purchaseTimezone: item.purchaseTimezone || getUserTimezone(),
        tags: item.tags || [],
        unitType: (item as any).unitType || '',
        unitSubtype: (item as any).unitSubtype || '',
        unitQuantity: (item as any).unitQuantity || '',
      });
      setPurchaseDateValue(item.purchaseDate ? new Date(item.purchaseDate) : null);
      setPurchaseTimeValue(item.purchaseTime || '');
      setTags(item.tags || []);
      setSelectedFiles([]);
      setPreviewUrls([]);
    } else {
      resetForm();
    }
  }, [item]);

  useEffect(() => {
    const fetchExistingImages = async () => {
      if (!item?.id) {
        setExistingImages([]);
        return;
      }

      try {
        const response = await api.get(`/items/${item.id}/images`);
        const images = Array.isArray(response.data) ? response.data : [];
        setExistingImages(images);
      } catch (error) {
        console.error('Failed to fetch item images:', error);
        setExistingImages([]);
      }
    };

    fetchExistingImages();
  }, [item?.id]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      categoryId: '',
      condition: 'N/A',
      location: '',
      purchasePlace: '',
      purchasePrice: '',
      purchaseCurrency: 'USD',
      purchaseDate: '',
      purchaseTime: '',
      purchaseTimezone: getUserTimezone(),
      tags: [],
      unitType: '',
      unitSubtype: '',
      unitQuantity: '',
    });
    setPurchaseDateValue(null);
    setPurchaseTimeValue('');
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setTags([]);
    setTagInput('');
    setPreviewUrls([]);
    setCategorySuggestions([]);
    setShowSuggestions(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear suggestions when user manually selects a category
    if (name === 'categoryId') {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (categoryId: string) => {
    setFormData(prev => ({ ...prev, categoryId }));
    setShowSuggestions(false);
  };

  const syncPurchaseDate = (dateValue: Date | null, timeValue: string) => {
    if (!dateValue) {
      setFormData(prev => ({ ...prev, purchaseDate: '' }));
      return;
    }
    const [h, m] = (timeValue || '00:00').split(':').map(Number);
    const date = new Date(dateValue);
    date.setHours(h || 0, m || 0, 0, 0);
    const iso = date.toISOString();
    setFormData(prev => ({ ...prev, purchaseDate: iso }));
  };

  const handlePurchaseDateValueChange = (value: Date | null) => {
    setPurchaseDateValue(value);
    syncPurchaseDate(value, purchaseTimeValue);
  };

  const handlePurchaseTimeValueChange = (value: string) => {
    setPurchaseTimeValue(value);
    setFormData(prev => ({ ...prev, purchaseTime: value }));
    syncPurchaseDate(purchaseDateValue, value);
  };

  const formatDisplayDate = (value: Date | null) => {
    if (!value) return t('itemModal.selectDate');
    return value.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const timeOptions = Array.from({ length: 24 * 4 }, (_, index) => {
    const hours = Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  });

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return '#11998e';
    if (confidence >= 0.6) return '#f59e0b';
    return '#a0aec0';
  };

  const getConfidenceLabel = (confidence: number): string => {
    if (confidence >= 0.8) return t('itemModal.highMatch');
    if (confidence >= 0.6) return t('itemModal.mediumMatch');
    return t('itemModal.lowMatch');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const currentCount = existingImages.length + selectedFiles.length;
      // Check plan image limit (-1 means unlimited)
      const limit = maxImages === -1 ? Infinity : maxImages;
      if (currentCount >= limit) {
        checkImageLimit(currentCount);
        return;
      }
      const newFiles = fileArray.slice(0, Math.max(0, limit - currentCount));
      
      const updatedFiles = [...selectedFiles, ...newFiles];
      setSelectedFiles(updatedFiles);
      
      // Generate preview URLs for new files
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrls(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
      
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = previewUrls.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    setPreviewUrls(updatedPreviews);
    
  };

  const handleRemoveExistingImage = async (imageId: string) => {
    if (!item?.id) return;
    try {
      await api.delete(`/items/${item.id}/images/${imageId}`);
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (error) {
      console.error('Failed to remove image:', error);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      setFormData(prev => ({ ...prev, tags: newTags }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag);
    setTags(newTags);
    setFormData(prev => ({ ...prev, tags: newTags }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData, selectedFiles);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Failed to save item:', error);
    } finally {
      setLoading(false);
    }
  };

  const existingPreviewImages = useMemo(() => {
    if (existingImages.length > 0) {
      return existingImages
        .map((img) => ({ id: img.id, url: resolveFileUrl(img.url) }))
        .filter((img) => !!img.url);
    }
    if (item?.imageUrl) {
      const resolved = resolveFileUrl(item.imageUrl);
      return resolved ? [{ id: 'primary', url: resolved }] : [];
    }
    return [] as Array<{ id: string; url: string }>
  }, [existingImages, item?.imageUrl]);

  const totalImageCount = existingPreviewImages.length + previewUrls.length;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{item ? t('itemModal.editItem') : t('itemModal.addNewItem')}</h2>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-grid">
            <div className="form-group full-width">
              <label>{t('item.name')} *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('item.namePlaceholder')}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>{t('item.description')}</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                placeholder={t('item.descriptionPlaceholder')}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>
                {t('item.category')}
                {suggestingCategories && (
                  <span className="ai-loading">
                    <FontAwesomeIcon icon={faSpinner} spin /> {t('itemModal.aiAnalyzing')}
                  </span>
                )}
              </label>
              <select
                name="categoryId"
                value={formData.categoryId || ''}
                onChange={handleChange}
              >
                <option value="">{t('item.selectCategory')}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{tc(cat.name)}</option>
                ))}
              </select>
              
              {/* AI Category Suggestions */}
              {showSuggestions && categorySuggestions.length > 0 && !formData.categoryId && (
                <div className="ai-suggestions">
                  <div className="ai-suggestions-header">
                    <FontAwesomeIcon icon={faRobot} />
                    <span>{t('itemModal.aiSuggestions')}</span>
                    <button 
                      type="button" 
                      className="dismiss-suggestions"
                      onClick={() => setShowSuggestions(false)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="suggestion-list">
                    {categorySuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        type="button"
                        className="suggestion-item"
                        onClick={() => handleSelectSuggestion(suggestion.id)}
                      >
                        <div className="suggestion-info">
                          <span className="suggestion-name">{tc(suggestion.name)}</span>
                          <span 
                            className="suggestion-confidence"
                            style={{ color: getConfidenceColor(suggestion.confidence) }}
                          >
                            <FontAwesomeIcon icon={faLightbulb} />
                            {getConfidenceLabel(suggestion.confidence)} ({Math.round(suggestion.confidence * 100)}%)
                          </span>
                        </div>
                        <div 
                          className="confidence-bar"
                          style={{ 
                            width: `${suggestion.confidence * 100}%`,
                            backgroundColor: getConfidenceColor(suggestion.confidence)
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>{t('item.condition')}</label>
              <select
                name="condition"
                value={formData.condition}
                onChange={handleChange}
              >
                <option value="N/A">N/A</option>
                <option value="NEW">{t('condition.new')}</option>
                <option value="LIKE_NEW">{t('condition.likeNew')}</option>
                <option value="GOOD">{t('condition.good')}</option>
                <option value="FAIR">{t('condition.fair')}</option>
                <option value="POOR">{t('condition.poor')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('item.location')}</label>
              <input
                type="text"
                name="location"
                value={formData.location || ''}
                onChange={handleChange}
                placeholder={t('item.locationPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label>{t('itemModal.purchasePlace')}</label>
              <input
                type="text"
                name="purchasePlace"
                value={formData.purchasePlace || ''}
                onChange={handleChange}
                placeholder={t('itemModal.purchasePlacePlaceholder')}
              />
            </div>

            <div className="form-group">
              <label>{t('item.purchasePrice')}</label>
              <input
                type="number"
                name="purchasePrice"
                value={formData.purchasePrice || ''}
                onChange={(e) => {
                  const raw = parseFloat(e.target.value);
                  const clamped = isNaN(raw) ? '' : Math.min(Math.max(0, raw), 999999999).toFixed(2).replace(/\.00$/, '');
                  setFormData(prev => ({ ...prev, purchasePrice: clamped }));
                }}
                placeholder="0.00"
                step="0.01"
                min="0"
                max="999999999"
              />
            </div>

            <div className="form-group">
              <label>{t('itemModal.currency')}</label>
              <select
                name="purchaseCurrency"
                value={formData.purchaseCurrency}
                onChange={handleChange}
              >
                {currencies.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.code}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '6px' }} />
                {t('itemModal.purchaseDateTime')}
              </label>
              <div className="date-time-row">
                <div className="date-picker">
                  <button
                    type="button"
                    className="date-picker-trigger"
                    onClick={() => {
                      setCalendarOpen(!calendarOpen);
                      setTimeOpen(false);
                    }}
                  >
                    <span>{formatDisplayDate(purchaseDateValue)}</span>
                    <FontAwesomeIcon icon={faCalendar} />
                  </button>
                </div>

                <div className="time-picker">
                  <button
                    type="button"
                    className="time-picker-trigger"
                    onClick={() => {
                      setTimeOpen(!timeOpen);
                      setCalendarOpen(false);
                    }}
                  >
                    <span>{purchaseTimeValue || t('itemModal.timeOptional')}</span>
                    <FontAwesomeIcon icon={faClock} />
                  </button>
                </div>
              </div>

              {(calendarOpen || timeOpen) && (
                <div className="date-time-panel">
                  {calendarOpen && (
                    <div className="calendar-panel">
                      <Calendar
                        onChange={(value) => {
                          const selected = Array.isArray(value) ? value[0] : value;
                          handlePurchaseDateValueChange(selected || null);
                          setCalendarOpen(false);
                        }}
                        value={purchaseDateValue}
                        locale="en-GB"
                      />
                    </div>
                  )}

                  {timeOpen && (
                    <div className="time-panel">
                      <button
                        type="button"
                        className={`time-option ${purchaseTimeValue === '' ? 'active' : ''}`}
                        onClick={() => {
                          handlePurchaseTimeValueChange('');
                          setTimeOpen(false);
                        }}
                      >
                        {t('itemModal.noTime')}
                      </button>
                      {timeOptions.map(option => (
                        <button
                          key={option}
                          type="button"
                          className={`time-option ${purchaseTimeValue === option ? 'active' : ''}`}
                          onClick={() => {
                            handlePurchaseTimeValueChange(option);
                            setTimeOpen(false);
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '6px' }} />
                {t('itemModal.timezone')}
              </label>
              <select
                name="purchaseTimezone"
                value={formData.purchaseTimezone}
                onChange={handleChange}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time</option>
                <option value="Pacific/Honolulu">Hawaii Time</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Central European (CET)</option>
                <option value="Europe/Berlin">Berlin (CET)</option>
                <option value="Europe/Prague">Prague (CET)</option>
                <option value="Europe/Moscow">Moscow (MSK)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
                <option value="Asia/Tokyo">Japan (JST)</option>
                <option value="Asia/Shanghai">China (CST)</option>
                <option value="Australia/Sydney">Sydney (AEST)</option>
                <option value="Pacific/Auckland">New Zealand (NZST)</option>
              </select>
              <small className="timezone-hint">{t('itemModal.autoDetected')}: {getUserTimezone()}</small>
            </div>

            {/* Unit of Measure */}
            <div className="form-group full-width unit-of-measure-section">
              <label>
                <FontAwesomeIcon icon={faRulerCombined} style={{ marginRight: '6px' }} />
                {t('unit.title')}
              </label>
              <div className="unit-row">
                <div className="unit-field">
                  <select
                    name="unitType"
                    value={formData.unitType || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, unitType: val, unitSubtype: '' }));
                    }}
                  >
                    <option value="">{t('unit.selectType')}</option>
                    {UNIT_TYPES.map(ut => (
                      <option key={ut.key} value={ut.key}>{t(`unit.type.${ut.key}` as any)}</option>
                    ))}
                  </select>
                </div>
                {formData.unitType && formData.unitType !== 'custom' && getSubtypes(formData.unitType).length > 0 && (
                  <div className="unit-field">
                    <select
                      name="unitSubtype"
                      value={formData.unitSubtype || ''}
                      onChange={handleChange}
                    >
                      <option value="">{t('unit.selectUnit')}</option>
                      {getSubtypes(formData.unitType).map(sub => (
                        <option key={sub.key} value={sub.key}>{t(`unit.sub.${sub.key}` as any)}</option>
                      ))}
                    </select>
                  </div>
                )}
                {formData.unitType === 'custom' && (
                  <div className="unit-field">
                    <input
                      type="text"
                      name="unitSubtype"
                      value={formData.unitSubtype || ''}
                      onChange={handleChange}
                      placeholder={t('unit.customUnit')}
                    />
                  </div>
                )}
                {formData.unitType && (
                  <div className="unit-field unit-qty">
                    <input
                      type="number"
                      name="unitQuantity"
                      value={formData.unitQuantity || ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setFormData(prev => ({ ...prev, unitQuantity: raw }));
                      }}
                      placeholder={t('unit.enterQuantity')}
                      step="any"
                      min="0"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>{t('item.images')}</label>
              <div className="file-upload">
                <input
                  type="file"
                  id="file-upload"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={maxImages !== -1 && totalImageCount >= maxImages}
                />
                <label htmlFor="file-upload" className="file-upload-label">
                  <FontAwesomeIcon icon={faUpload} />
                  {totalImageCount === 0 ? t('item.chooseImages') : `${totalImageCount}/${maxImages === -1 ? '∞' : maxImages} ${t('item.images')}`}
                </label>

                {existingPreviewImages.length > 0 && (
                  <div className="images-preview-grid">
                    {existingPreviewImages.map((img, index) => (
                      <div key={img.id} className="image-preview-item">
                        <img src={img.url || undefined} alt={`Saved ${index + 1}`} />
                        {img.id !== 'primary' && (
                          <button
                            type="button"
                            className="remove-image-btn"
                            onClick={() => handleRemoveExistingImage(img.id)}
                            title="Remove this image"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                        <span className="image-index">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                )}

                {previewUrls.length > 0 && (
                  <div className="images-preview-grid">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="image-preview-item">
                        <img src={url} alt={`Preview ${index + 1}`} />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => handleRemoveImage(index)}
                          title="Remove this image"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <span className="image-index">{existingPreviewImages.length + index + 1}</span>
                      </div>
                    ))}
                    {(maxImages === -1 || totalImageCount < maxImages) && (
                      <label htmlFor="file-upload" className="add-more-images">
                        <FontAwesomeIcon icon={faUpload} />
                        <span>{t('item.addMore')}</span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>{t('item.tags')}</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder={t('item.tagsPlaceholder')}
                />
                <button type="button" onClick={handleAddTag} className="btn-add-tag">
                  <FontAwesomeIcon icon={faTag} /> {t('itemModal.add')}
                </button>
              </div>
              <div className="tags-list">
                {tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              {t('action.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? t('itemModal.saving') : (item ? t('itemModal.updateItem') : t('itemModal.createItem'))}
            </button>
          </div>
        </form>
      </div>

      {/* Plan Limit Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={closeLimitModal}
        limitType={limitModalType}
        currentUsage={limitCurrent}
        maxAllowed={limitMax}
        planName={currentPlan}
      />
    </div>
  );
};

export default ItemModal;
